import { useCallback, useEffect, useState } from 'react';
import { Check, Copy, Loader2, UserPlus, X } from 'lucide-react';
import type { P2pProvider } from '../sync/p2pProvider';
import { Button } from '../components/Button';
import { MAX_PEERS } from '../sync/types';
import { SOFT_LENGTH_LIMIT } from '../sync/signalingCodec';

interface Props {
  provider: P2pProvider;
}

interface PendingInviteView {
  peerId: string;
  url: string;
  blob: string;
  createdAt: number;
}

// Floating "Invite" button at top-left of the canvas column. Click expands
// the panel inline (modal would steal focus from the canvas while waiting
// for a paste-back). Three sections:
//   1. Step 1 — Share this link (generated on click; one per guest)
//   2. Step 2 — Paste the code they send back
//   3. Connected peers count
export function InvitePanel({ provider }: Props) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<PendingInviteView[]>([]);
  const [peerCount, setPeerCount] = useState(provider.listPeers().length);
  const [generating, setGenerating] = useState(false);
  const [answerInput, setAnswerInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const refresh = () => {
      setPeerCount(provider.listPeers().length);
    };
    provider.on('peers-changed', refresh);
    return () => provider.off('peers-changed', refresh);
  }, [provider]);

  const generateInvite = useCallback(async () => {
    setError(null);
    setGenerating(true);
    try {
      const { peerId, offerCode } = await provider.createInvite();
      // BoardPage reads `#join=<code>` from the fragment when the guest opens
      // the URL. Fragment (not query) keeps the code out of the network path
      // — fragments don't leave the browser. Reusing the host's board URL
      // means the guest's IndexedDB ends up under the same boardId namespace.
      const path = `/b/${encodeURIComponent(boardIdFromLocation())}`;
      const url = `${window.location.origin}${path}#join=${encodeURIComponent(offerCode)}`;
      setPending((prev) => [
        ...prev,
        { peerId, url, blob: offerCode, createdAt: Date.now() },
      ]);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setGenerating(false);
    }
  }, [provider]);

  const cancelInvite = useCallback(
    (peerId: string) => {
      provider.cancelInvite(peerId);
      setPending((prev) => prev.filter((p) => p.peerId !== peerId));
    },
    [provider],
  );

  const submitAnswer = useCallback(async () => {
    const trimmed = answerInput.trim();
    if (!trimmed) return;
    setError(null);
    setBusy(true);
    try {
      await provider.acceptAnswer(trimmed);
      setAnswerInput('');
      // Clean up the oldest pending invite — that's the one we just paired.
      setPending((prev) => prev.slice(1));
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  }, [answerInput, provider]);

  const atCap = peerCount + pending.length >= MAX_PEERS;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Invite people to collaborate"
        className="
          absolute top-3 left-3 z-[3]
          h-control px-3 inline-flex items-center gap-1.5
          bg-bg-panel border border-border-strong rounded-2
          text-13 font-medium text-fg-primary
          hover:bg-bg-hover transition-colors duration-micro ease-out
          focus-visible:outline-none focus-visible:shadow-focus
        "
      >
        <UserPlus size={14} strokeWidth={1.5} />
        Invite
        {peerCount > 0 && (
          <span className="font-mono text-12 tabular-nums text-fg-tertiary">
            · {peerCount}
          </span>
        )}
      </button>
    );
  }

  return (
    <div
      className="
        absolute top-3 left-3 z-[3]
        w-[360px] max-h-[calc(100%-24px)] overflow-y-auto
        bg-bg-panel border border-border-strong rounded-3
        shadow-md
      "
    >
      <header className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
        <div className="flex items-center gap-2">
          <UserPlus size={14} strokeWidth={1.5} className="text-fg-secondary" />
          <strong className="text-13 font-medium text-fg-primary">Invite people</strong>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close"
          className="w-6 h-6 inline-flex items-center justify-center rounded-2 text-fg-tertiary hover:bg-bg-hover hover:text-fg-primary"
        >
          <X size={14} strokeWidth={1.5} />
        </button>
      </header>

      <div className="px-4 py-3 flex flex-col gap-3">
        <Section
          title="Step 1 · Share a link"
          hint="Each link works for one guest. They'll send back a short code to finish joining."
        >
          <Button
            variant="primary"
            size="sm"
            onClick={generateInvite}
            disabled={generating || atCap}
            title={atCap ? `Room is full (${MAX_PEERS} peers)` : 'Generate a fresh invite link'}
          >
            {generating ? (
              <>
                <Loader2 size={12} strokeWidth={1.5} className="animate-spin" />
                Generating…
              </>
            ) : (
              'Generate invite link'
            )}
          </Button>
          {atCap && (
            <p className="text-12 text-fg-tertiary leading-[1.4]">
              Room is full ({MAX_PEERS} peers). Remove someone first.
            </p>
          )}
          {pending.length > 0 && (
            <ul className="m-0 p-0 list-none flex flex-col gap-2">
              {pending.map((p) => (
                <PendingInviteCard
                  key={p.peerId}
                  invite={p}
                  onCancel={() => cancelInvite(p.peerId)}
                />
              ))}
            </ul>
          )}
        </Section>

        <Section
          title="Step 2 · Paste their reply"
          hint="Drop the code they sent you here to complete the connection."
        >
          <textarea
            value={answerInput}
            onChange={(e) => setAnswerInput(e.target.value)}
            placeholder="v1.a.…"
            spellCheck={false}
            rows={3}
            className="
              w-full px-2.5 py-1.5
              bg-bg-subtle border border-border-default rounded-2
              font-mono text-12 text-fg-primary
              focus-visible:outline-none focus-visible:shadow-focus
              resize-none
            "
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={submitAnswer}
            disabled={busy || !answerInput.trim()}
          >
            {busy ? 'Connecting…' : 'Approve join'}
          </Button>
        </Section>

        {error && (
          <p className="text-12 text-error leading-[1.4]" role="alert">
            {error}
          </p>
        )}

        <footer className="text-12 text-fg-tertiary leading-[1.4] border-t border-border-subtle pt-3">
          Connected peers: <span className="font-mono tabular-nums text-fg-primary">{peerCount}</span> / {MAX_PEERS}
        </footer>
      </div>
    </div>
  );
}

function PendingInviteCard({
  invite,
  onCancel,
}: {
  invite: PendingInviteView;
  onCancel: () => void;
}) {
  const [copied, setCopied] = useState<'url' | 'blob' | null>(null);
  const overflow = invite.url.length > SOFT_LENGTH_LIMIT;

  const copy = useCallback(async (text: string, kind: 'url' | 'blob') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied((c) => (c === kind ? null : c)), 1500);
    } catch {
      /* clipboard blocked; user can manually select */
    }
  }, []);

  return (
    <li className="bg-bg-subtle border border-border-default rounded-2 p-2.5 flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <input
          readOnly
          value={overflow ? invite.blob : invite.url}
          onFocus={(e) => e.currentTarget.select()}
          className="
            flex-1 min-w-0 px-2 py-1
            bg-bg-app border border-border-default rounded-1
            font-mono text-12 text-fg-secondary
            focus-visible:outline-none focus-visible:shadow-focus
          "
        />
        <button
          type="button"
          onClick={() => copy(overflow ? invite.blob : invite.url, overflow ? 'blob' : 'url')}
          title="Copy"
          className="w-7 h-7 inline-flex items-center justify-center rounded-2 text-fg-tertiary hover:bg-bg-hover hover:text-fg-primary"
        >
          {copied ? <Check size={14} strokeWidth={1.5} /> : <Copy size={14} strokeWidth={1.5} />}
        </button>
        <button
          type="button"
          onClick={onCancel}
          title="Cancel invite"
          className="w-7 h-7 inline-flex items-center justify-center rounded-2 text-fg-tertiary hover:bg-error-subtle hover:text-error"
        >
          <X size={14} strokeWidth={1.5} />
        </button>
      </div>
      {overflow && (
        <p className="text-12 text-fg-tertiary leading-[1.3]">
          Link is too long for some chat apps. Send this code instead — the guest can paste it on
          the join page.
        </p>
      )}
    </li>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="font-mono text-12 font-medium uppercase tracking-[0.02em] text-fg-tertiary">
        {title}
      </h3>
      <p className="text-12 text-fg-tertiary leading-[1.4]">{hint}</p>
      {children}
    </section>
  );
}

function messageOf(e: unknown): string {
  if (e instanceof Error) return e.message;
  return 'Something went wrong.';
}

// Pull the current board id out of the URL path so we don't have to plumb
// it through props. The path is always `/b/<id>` for a live board (see
// App.tsx routing); fallback is 'untitled' for the never-routed edge case.
function boardIdFromLocation(): string {
  if (typeof window === 'undefined') return 'untitled';
  const match = window.location.pathname.match(/^\/b\/([^/]+)/);
  return match ? decodeURIComponent(match[1]!) : 'untitled';
}
